import React from "react"
import strings from "../../../localization/strings"
import { ISchedulePreference, IUserDocument, EDayOfWeek, IDate } from "../../../models"
import TimeActionBar from "../../../components/common/TimeActionBar/TimeActionBar"
import autobind from "class-autobind"
import moment from "moment"
import AppStateStore from "../../../stores/AppStateStore"
import * as timeHelper from "../../../helpers/timeHelper"
import TableActionBar from "../../../components/layout/TableActionBar"
import { FloatingActionButton, CircularProgress } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import UserDetailWorkPreferenceTable from "../../../components/work_preference/UserDetailWorkPreferenceTable"
import AddUserDetailSchedulePreferenceDialog from "../../../components/work_preference/AddUserDetailSchedulePreferenceDialog"
import * as api from "../../../api"

interface IProps {
    user: IUserDocument
    scheduleStartDay: EDayOfWeek
    readonly: boolean
}
interface IState {
    loading: boolean
    referenceDate: IDate
    preferences: ISchedulePreference[]
    filteredPreferences: ISchedulePreference[]
    selectedSchedulePreferences: ISchedulePreference[]
    selectedSchedulePreferenceForEdit: ISchedulePreference
    openAddSchedulePreferenceDialog: boolean
}

export default class UserDetailWorkPreferences extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: true,
            referenceDate: moment().format(),
            preferences: [],
            filteredPreferences: [],
            selectedSchedulePreferences: [],
            selectedSchedulePreferenceForEdit: null,
            openAddSchedulePreferenceDialog: false,
        }
        autobind(this)
    }

    componentDidMount() {
        this.getWorkPreferences()
    }

    getWorkPreferences() {
        api.getSchedulePreference(this.props.user.Id).then(res =>
            this.setState({
                preferences: res.data,
                selectedSchedulePreferences: []
            }, () => this.getFilteredPreferences())
        )
    }

    async getFilteredPreferences() {
        const scheduleWeek = timeHelper.getWeekRange(EDayOfWeek.Monday, this.state.referenceDate)
        const filteredPreferences = await Promise.all(
            this.state.preferences.map(async item => {
                const localStartTime = await timeHelper.getDateTime(this.props.user.DefaultPunchHierarchyId, this.props.user.DefaultJobId, moment(item.StartTime).format())
                const localEndTime = await timeHelper.getDateTime(this.props.user.DefaultPunchHierarchyId, this.props.user.DefaultJobId, moment(item.EndTime).format())
                if (moment(scheduleWeek.startDate).isAfter(localEndTime)) {
                    return null
                }
                else if (moment(scheduleWeek.endDate).isBefore(localStartTime)) {
                    return null
                }
                return item
            })
        )

        this.setState({ loading: false, filteredPreferences: filteredPreferences.filter(i => i !== null) })
    }

    render() {
        return (
            <div style={{ padding: 30 }}>
                {!this.props.readonly &&
                    <FloatingActionButton data-intercom-target={"menu-Employees-WorkPref-Btn-Add"} className="fixed-add" style={{ position: "relative", float: "right", top: -45, right: 50, zIndex: 900 }} secondary={true} onClick={() => this.setState({ openAddSchedulePreferenceDialog: true })}>
                        <ContentAdd />
                    </FloatingActionButton>
                }
                <TableActionBar
                    searchText=""
                    rowSelectionCount={this.state.selectedSchedulePreferences.length}
                    singleSelectedMessage={strings.Preference_Selected}
                    multipleSelectedMessage={strings.x_Preference_Selected}
                    title={strings.Work_Preferences}
                    onDeleteClick={() =>
                        AppStateStore.setConfirmDialogContent({
                            content: strings.Are_you_sure_you_want_to_delete_selected_preference,
                            confirm: () => {
                                AppStateStore.setProgressSnackbarContent("loading")
                                const schedulePreferenceIds = this.state.selectedSchedulePreferences.map(item => item.Id)
                                api.deleteSchedulePreferences(this.props.user.Id, schedulePreferenceIds).then(res => {
                                    this.setState({
                                        preferences: res.data,
                                        selectedSchedulePreferences: []
                                    }, () => this.getFilteredPreferences())
                                    AppStateStore.setProgressSnackbarContent(strings.Successfully_deleted)
                                })
                            }
                        })
                    }
                />
                <TimeActionBar
                    referenceDate={this.state.referenceDate}
                    handleChangeTimeRange={(startDate, endDate) => this.setState({ loading: true, referenceDate: startDate, filteredPreferences: [] }, () => this.getFilteredPreferences())}
                    startDayOfWeek={this.props.scheduleStartDay}
                    timeBarViewType={"week"}
                />
                {this.state.loading ?
                    <div className="spinner">
                        <CircularProgress />
                    </div> :
                    this.state.filteredPreferences.length === 0 ?
                        <h2 style={{ textAlign: "center", marginTop: 25 }}>
                            {strings.No_preference_found}
                        </h2> :
                        <div style={{ marginTop: -20 }}>
                            <UserDetailWorkPreferenceTable
                                readonly={this.props.readonly}
                                user={this.props.user}
                                schedulePreferences={this.state.filteredPreferences}
                                selectedSchedulePreferences={this.state.selectedSchedulePreferences}
                                handleSelect={selectedSchedulePreferences => this.setState({ selectedSchedulePreferences })}
                                handleUpdate={selectedSchedulePreferenceForEdit =>
                                    !this.props.readonly &&
                                    this.setState({ selectedSchedulePreferenceForEdit }, () => this.setState({ openAddSchedulePreferenceDialog: true }))
                                }
                            />
                        </div>
                }
                {this.state.openAddSchedulePreferenceDialog &&
                    <AddUserDetailSchedulePreferenceDialog
                        employee={this.props.user}
                        existingSchedulePreference={this.state.preferences}
                        editSchedulePreference={this.state.selectedSchedulePreferenceForEdit}
                        handleClose={() => this.setState({ openAddSchedulePreferenceDialog: false, selectedSchedulePreferenceForEdit: null })}
                        handleAdd={schedulePreference => {
                            AppStateStore.setProgressSnackbarContent("loading")
                            api.addSchedulePreference(schedulePreference, this.props.user.Id).then(res => {
                                this.setState({
                                    preferences: res.data,
                                    selectedSchedulePreferences: []
                                }, () => this.getFilteredPreferences())
                                AppStateStore.setProgressSnackbarContent(strings.Successfully_created)
                            })
                        }}
                        handleUpdate={schedulePreference => {
                            AppStateStore.setProgressSnackbarContent("loading")
                            api.updateSchedulePreference(schedulePreference, this.props.user.Id).then(res => {
                                this.setState({
                                    preferences: res.data,
                                    selectedSchedulePreferences: []
                                }, () => this.getFilteredPreferences())
                                AppStateStore.setProgressSnackbarContent(strings.Successfully_updated)
                            })
                        }}
                    />
                }
            </div>
        )
    }
}